// Admin API Client
import { apiRequest } from './api';

export const AdminAPI = {
    /**
     * Get system stats
     */
    getStats: (token: string) =>
        apiRequest<{
            overview: {
                totalUsers: number;
                totalAdmins: number;
                totalClasses: number;
                totalQuizzes: number;
                totalSessions: number;
                activeUsers: number;
                recentRegistrations: number;
                publicClasses: number;
                publishedQuizzes: number;
            };
            recentUsers: any[];
            mostActiveUsers: any[];
        }>('/admin/stats', { token }),

    /**
     * List all users with pagination
     */
    getUsers: (params: {
        page?: number;
        limit?: number;
        search?: string;
        role?: 'user' | 'admin' | '';
        sort?: string;
        order?: 'ASC' | 'DESC';
    }, token: string) => {
        const query = new URLSearchParams();
        if (params.page) query.set('page', String(params.page));
        if (params.limit) query.set('limit', String(params.limit));
        if (params.search) query.set('search', params.search);
        if (params.role) query.set('role', params.role);
        if (params.sort) query.set('sort', params.sort);
        if (params.order) query.set('order', params.order);

        return apiRequest<{
            users: any[];
            pagination: {
                page: number;
                limit: number;
                total: number;
                totalPages: number;
            };
        }>(`/admin/users?${query.toString()}`, { token });
    },

    /**
     * Get user detail
     */
    getUserDetail: (userId: string, token: string) =>
        apiRequest<any>(`/admin/users/${userId}`, { token }),

    /**
     * Delete user
     */
    deleteUser: (userId: string, token: string) =>
        apiRequest<{ message: string }>(`/admin/users/${userId}`, {
            method: 'DELETE',
            token,
        }),

    /**
     * Update user role
     */
    updateUserRole: (userId: string, role: 'user' | 'admin', token: string) =>
        apiRequest<{ message: string; user: any }>(`/admin/users/${userId}/role`, {
            method: 'PUT',
            token,
            body: JSON.stringify({ role }),
        }),

    /**
     * Update user name
     */
    updateUserName: (userId: string, name: string, token: string) =>
        apiRequest<{ message: string; user: any }>(`/admin/users/${userId}/name`, {
            method: 'PUT',
            token,
            body: JSON.stringify({ name }),
        }),

    /**
     * Reset user password
     */
    resetUserPassword: (userId: string, token: string) =>
        apiRequest<{ message: string; defaultPassword: string }>(`/admin/users/${userId}/reset-password`, {
            method: 'POST',
            token,
        }),

    /**
     * Update user secondary role
     */
    updateSecondaryRole: (userId: string, secondaryRole: string | null, token: string) =>
        apiRequest<{ message: string; user: any }>(`/admin/users/${userId}/secondary-role`, {
            method: 'PUT',
            token,
            body: JSON.stringify({ secondaryRole }),
        }),

    /**
     * Delete all user messages
     */
    deleteUserMessages: (userId: string, token: string) =>
        apiRequest<{ message: string; deletedCount: number }>(`/admin/users/${userId}/messages`, {
            method: 'DELETE',
            token,
        }),

    /**
     * Get user content (classes & quizzes)
     */
    getUserContent: (userId: string, token: string) =>
        apiRequest<{ user: any; classes: any[]; quizzes: any[] }>(`/admin/users/${userId}/content`, { token }),

    /**
     * Delete all user content
     */
    deleteUserContent: (userId: string, token: string) =>
        apiRequest<{ message: string; deletedClasses: number; deletedQuizzes: number }>(`/admin/users/${userId}/content`, {
            method: 'DELETE',
            token,
        }),

    /**
     * Get public content
     */
    getPublicContent: (token: string) =>
        apiRequest<{ publicClasses: any[]; publishedQuizzes: any[] }>('/admin/public', { token }),

    /**
     * Toggle class public status
     */
    toggleClassPublic: (classId: string, token: string) =>
        apiRequest<{ message: string; class: any }>(`/admin/public/class/${classId}`, {
            method: 'PUT',
            token,
        }),

    /**
     * Toggle quiz published status
     */
    toggleQuizPublished: (quizId: string, token: string) =>
        apiRequest<{ message: string; quiz: any }>(`/admin/public/quiz/${quizId}`, {
            method: 'PUT',
            token,
        }),

    /**
     * Clean up chat history (files and messages)
     */
    cleanupChat: (days: number, token: string) =>
        apiRequest<{ message: string; deleted_count: number }>(`/admin/chat/cleanup`, {
            method: 'POST',
            token,
            body: JSON.stringify({ days }),
        }),
};
